/**
 * ConciergeOSShell — Admin Phase 1a · the Control Room frame.
 *
 * Per Concierge_OS_Admin_Build.md §4·1a:
 *   "The shell loads, every nav item resolves, the dashboard renders
 *    the 8 cards, role chip shows, global search bar present. No
 *    throwaway screens — this shell IS the permanent frame."
 *
 * Role-aware grouped sidebar (Operate / Modules / Quality&Safety /
 * Operations / System). Renders /admin/* sub-routes via <Outlet/>.
 *
 * Auth: hydrates /api/os-admin/me on mount so the sidebar groups are
 * gated by the BACKEND-resolved role (visibility map), not by a
 * client-only flag.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import "@/styles/os-admin.css";

// ── nav definition · single source of truth (also drives breadcrumbs)
const NAV_GROUPS = [
  {
    key: "operate",
    label: "Operate",
    items: [
      { to: "/admin", end: true, label: "Dashboard", ic: "▦" },
      { to: "/admin/operate/requests", label: "Requests", ic: "◆" },
      { to: "/admin/operate/anything", label: "Anything Desk", ic: "✳" },
      { to: "/admin/operate/goldenbook", label: "Golden Book Desk", ic: "❖" },
      { to: "/admin/operate/goldenbook-notes", label: "Golden Book Notes", ic: "✎" },
      { to: "/admin/operate/fyi", label: "Self-serve FYI", ic: "⟳" },
      { to: "/admin/operate/publish-gate", label: "Publish Gate", ic: "▷" },
      { to: "/admin/operate/catalogue/enjoy-signature", label: "Enjoy Signature (K1)", ic: "✦" },
      { to: "/admin/operate/catalogue/enjoy-overlay", label: "Enjoy Overlay (K2)", ic: "✦" },
      { to: "/admin/operate/catalogue/table-signature", label: "Table Signature (K3)", ic: "✦" },
      { to: "/admin/operate/catalogue/escape-signature", label: "Escape Signature (K4)", ic: "✦" },
      { to: "/admin/operate/mira", label: "Mira Inbox", ic: "✦" },
      { to: "/admin/operate/enquiries", label: "Legacy Enquiries", ic: "✉" },
    ],
  },
  {
    key: "modules",
    label: "Modules",
    items: [
      { to: "/admin/modules/table", label: "Table", ic: "▤" },
      { to: "/admin/modules/stay", label: "Stay", ic: "▤" },
      { to: "/admin/modules/fit", label: "Fit", ic: "▤" },
      { to: "/admin/modules/enjoy", label: "Enjoy", ic: "▤" },
      { to: "/admin/modules/escape", label: "Escape", ic: "▤" },
      { to: "/admin/modules/airport", label: "Airport", ic: "▤" },
      { to: "/admin/modules/mobility", label: "Mobility", ic: "▤" },
      { to: "/admin/modules/club", label: "Club", ic: "▤" },
      { to: "/admin/modules/exclusive", label: "Exclusive · Maison", ic: "▤" },
    ],
  },
  {
    key: "quality_safety",
    label: "Quality & Safety",
    items: [
      { to: "/admin/quality/media", label: "Media Library", ic: "⬡", placeholder: "P2" },
      { to: "/admin/quality/source-audit", label: "Source Audit", ic: "⚲", placeholder: "P2" },
      { to: "/admin/quality/safety", label: "Safety Review", ic: "⚠", placeholder: "P2" },
      { to: "/admin/quality/imports", label: "Imports", ic: "⇪", placeholder: "P2" },
    ],
  },
  {
    key: "operations",
    label: "Operations",
    items: [
      { to: "/admin/operations/providers", label: "Providers", ic: "◈", placeholder: "P2" },
      { to: "/admin/operations/zoho", label: "Zoho Centre", ic: "⊞", placeholder: "P2" },
      { to: "/admin/operations/members", label: "Members", ic: "▥" },
      { to: "/admin/operations/aurelion-routing", label: "Aurelion Routing", ic: "⇆" },
      { to: "/admin/operations/reports", label: "Reports", ic: "▣", placeholder: "P2" },
      { to: "/admin/secrets/export", label: "Master Secrets Vault", ic: "🔐" },
    ],
  },
  {
    key: "system",
    label: "System",
    items: [
      { to: "/admin/system/content", label: "Site Content", ic: "✎" },
      { to: "/admin/system/doctrine", label: "Doctrine Docs", ic: "❡" },
      { to: "/admin/system/admin-urls", label: "Admin URLs", ic: "⌘" },
      { to: "/admin/system/webhook-log", label: "Webhook Log", ic: "⎙" },
      { to: "/admin/system/health", label: "System Health", ic: "♥", placeholder: "P2" },
      { to: "/admin/system/audit-log", label: "Audit Log", ic: "⌬", placeholder: "P2" },
      { to: "/admin/system/migration-map", label: "Migration Map", ic: "❖" },
      { to: "/admin/system/sitevault", label: "SiteVault Docs", ic: "⇩", founderOnly: true },
      { to: "/admin/system/users", label: "Role Admin", ic: "☷", founderOnly: true },
    ],
  },
];

// shared context — used by child pages to render a breadcrumb without
// recomputing the path.
export const OSContext = React.createContext({
  me: null,
  visibility: {},
  refresh: () => {},
});

export default function ConciergeOSShell() {
  const { admin, loading, logout } = useAuth();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  // load OS role + visibility from backend (backend is source of truth)
  useEffect(() => {
    if (!admin) return;
    (async () => {
      try {
        const { data } = await api.get("/os-admin/me");
        setMe(data);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, [admin]);

  // close mobile drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const visibility = me?.visibility || {};
  const osRole = me?.os_role || "";

  const visibleGroups = NAV_GROUPS.filter((g) => {
    // While me is loading we show Operate + Modules so the page isn't
    // blank · the rest is gated by backend visibility map.
    if (!me) return ["operate", "modules"].includes(g.key);
    return visibility[g.key] !== false;
  }).map((g) => ({
    ...g,
    items: g.items.filter((it) => !it.founderOnly || osRole === "founder"),
  }));

  // breadcrumb: derive from URL · "/admin/modules/escape" → "Modules / Escape"
  const crumb = computeCrumb(location.pathname);
  const initials = (admin.email || "").trim()[0]?.toUpperCase() || "·";

  return (
    <OSContext.Provider value={{ me, visibility, refresh: () => setMe(null) }}>
      <section className="os-admin" data-testid="os-admin-shell">
        <div className="os-shell">
          {/* sidebar */}
          <aside
            className={`os-rail ${drawerOpen ? "open" : ""}`}
            data-testid="os-admin-sidebar"
          >
            <div className="os-brand">
              <div className="key">
                CONCIERGE<span className="reg">®</span> OS
              </div>
              <div className="sub">Admin · Control Room</div>
            </div>
            {visibleGroups.map((group) => (
              <div className="os-navgroup" key={group.key} data-testid={`os-nav-group-${group.key}`}>
                <div className="os-navgroup-label">{group.label}</div>
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end || false}
                    className={({ isActive }) => `os-navitem ${isActive ? "active" : ""}`}
                    data-testid={`os-nav-${slugify(item.to)}`}
                  >
                    <span className="ic" aria-hidden>{item.ic}</span>
                    <span>{item.label}</span>
                    {item.placeholder && (
                      <span className="badge placeholder">{item.placeholder}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            ))}
          </aside>

          {/* main */}
          <main className="os-main">
            <div className="os-topbar">
              <button
                className="os-burger"
                aria-label="Menu"
                onClick={() => setDrawerOpen((v) => !v)}
                data-testid="os-burger"
              >
                ☰
              </button>
              <div className="os-crumb" data-testid="os-crumb">
                {crumb.map((c, i) => (
                  <span key={i}>
                    {i > 0 && " / "}
                    {i === crumb.length - 1 ? <b>{c}</b> : c}
                  </span>
                ))}
              </div>
              <div className="os-gsearch" data-testid="os-global-search">
                <span aria-hidden>⌕</span>
                <input
                  placeholder="Search requests, members, venues, tickets…"
                  disabled
                  title="Global search lands with Admin Phase 2"
                />
              </div>
              <div className="os-role-chip" data-testid="os-role-chip">
                <span className="av">{initials}</span>
                <span>{admin.email}</span>
                <span className="role-tag">{osRole || "no-role"}</span>
              </div>
              <button
                className="os-signout"
                onClick={logout}
                data-testid="os-signout"
              >
                Sign out
              </button>
            </div>

            <div className="os-content">
              {err && (
                <div
                  style={{
                    background: "rgba(199,106,91,0.08)",
                    border: "1px solid rgba(199,106,91,0.4)",
                    color: "#E5B6AE",
                    padding: "10px 14px",
                    borderRadius: 6,
                    marginBottom: 16,
                    fontSize: 12.5,
                  }}
                  data-testid="os-shell-error"
                >
                  {err}
                </div>
              )}
              <Outlet />
            </div>
          </main>
        </div>
      </section>
    </OSContext.Provider>
  );
}

function slugify(path) {
  return path.replace(/^\//, "").replace(/\//g, "-") || "root";
}

function computeCrumb(pathname) {
  // /admin                          → ["Operate", "Dashboard"]
  // /admin/operate/requests         → ["Operate", "Requests"]
  // /admin/modules/escape           → ["Modules", "Escape"]
  // /admin/system/migration-map     → ["System", "Migration Map"]
  if (pathname === "/admin" || pathname === "/admin/") {
    return ["Operate", "Dashboard"];
  }
  const parts = pathname.replace(/^\/admin\/?/, "").split("/").filter(Boolean);
  if (parts.length === 0) return ["Operate", "Dashboard"];
  const group = humanize(parts[0]);
  const leaf = humanize(parts.slice(1).join(" "));
  return [group, leaf];
}

function humanize(s) {
  return s
    .split(/[-_/]/)
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}
